import { useEffect, useRef, useState } from 'react';
import { bootcampWorkflowShader } from './bootcampWorkflowShader';

const steps = [
  { name: 'Research', text: 'Turn market questions into a focused research workflow.' },
  { name: 'Review', text: 'Check the evidence, broker data, and risks before acting.' },
  { name: 'Approve', text: 'You make the decision. Your agent works within your rules.' },
];

export default function BootcampWorkflow() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stageRef = useRef(0);
  const [stage, setStage] = useState(0);
  const interactionUntil = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (!('gpu' in navigator)) {
      canvas.dataset.gpuState = 'unsupported';
      return;
    }
    let cancelled = false;
    let dispose: (() => void) | undefined;
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    void import('vgpu')
      .then(async (vgpu) => {
        const gpu = await vgpu.init();
        if (cancelled) {
          gpu.dispose();
          return;
        }
        try {
          const target = vgpu.surface(gpu, canvas, { dpr: [1, 1.5], alphaMode: 'premultiplied' });
          const shader = vgpu.effect(gpu, bootcampWorkflowShader, {
            set: {
              params: {
                time: 0,
                stage: 0,
                aspect: 1,
                pointer: 0,
                ink: [0.55, 0.61, 0.7],
              },
            },
          });
          vgpu.frame(gpu, (frame) => frame.pass(target, shader));
          if (cancelled) {
            gpu.dispose();
            return;
          }
          let visible = true;
          let pointer = 0;
          let currentPointer = 0;
          let currentStage = 0;
          let time = 0;
          let lastFrame = 0;
          let dirty = true;
          const resize = target.onResize(() => {
            dirty = true;
          });
          const onPointer = (event: PointerEvent) => {
            pointer =
              (event.clientY - canvas.getBoundingClientRect().top) / canvas.clientHeight - 0.5;
            interactionUntil.current = performance.now() + 700;
            dirty = true;
          };
          const resetPointer = () => {
            pointer = 0;
            dirty = true;
          };
          canvas.addEventListener('pointermove', onPointer);
          canvas.addEventListener('pointerleave', resetPointer);
          const observer = new IntersectionObserver(([entry]) => {
            visible = entry.isIntersecting;
          });
          observer.observe(canvas);
          const theme = () => {
            const light =
              document.documentElement.dataset.bsTheme === 'light' ||
              document.documentElement.dataset.theme === 'light';
            shader.set({ params: { ink: light ? [0.21, 0.28, 0.38] : [0.59, 0.66, 0.77] } });
            dirty = true;
          };
          const themeObserver = new MutationObserver(theme);
          themeObserver.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['data-theme', 'data-bs-theme'],
          });
          theme();
          const loop = vgpu.frameLoop(
            gpu,
            (frame) => {
              const now = performance.now();
              const dt = Math.min((now - lastFrame) / 1000, 0.05);
              lastFrame = now;
              if (!visible || document.hidden) return;
              const moving = !motion.matches && (time < 4 || now < interactionUntil.current);
              canvas.dataset.motionState = moving ? 'moving' : 'settled';
              if (!moving && !dirty && currentStage === stageRef.current) return;
              if (moving) time += dt;
              currentStage = moving
                ? currentStage + (stageRef.current - currentStage) * 0.12
                : stageRef.current;
              if (Math.abs(currentStage - stageRef.current) < 0.001)
                currentStage = stageRef.current;
              currentPointer = moving ? currentPointer + (pointer - currentPointer) * 0.08 : 0;
              shader.set({ params: { time, stage: currentStage, pointer: currentPointer } });
              frame.pass(target, shader);
              canvas.dataset.rendered = 'true';
              dirty = false;
            },
            { fps: 30 },
          );
          dispose = () => {
            loop.stop();
            resize();
            observer.disconnect();
            themeObserver.disconnect();
            canvas.removeEventListener('pointermove', onPointer);
            canvas.removeEventListener('pointerleave', resetPointer);
            gpu.dispose();
          };
        } catch (error) {
          gpu.dispose();
          throw error;
        }
      })
      .catch((error: Error) => {
        canvas.dataset.gpuState = 'fallback';
        canvas.dataset.gpuError = error.message;
      });
    return () => {
      cancelled = true;
      dispose?.();
    };
  }, []);

  return (
    <figure className="v2-workflow" aria-label="Explore the course workflow">
      <div className="v2-workflow-visual">
        <svg className="v2-workflow-fallback" viewBox="0 0 500 300" aria-hidden="true">
          {Array.from({ length: 18 }, (_, i) => (
            <path
              key={i}
              d={`M 20 150 C 140 ${15 + i * 12}, 340 ${285 - i * 12}, 480 150`}
              fill="none"
              stroke="currentColor"
              strokeWidth="1"
              opacity=".45"
            />
          ))}
        </svg>
        <canvas ref={canvasRef} aria-hidden="true" />
      </div>
      <div className="v2-workflow-steps" aria-label="Workflow stages">
        {steps.map((step, index) => (
          <button
            key={step.name}
            type="button"
            aria-pressed={stage === index}
            onClick={() => {
              interactionUntil.current = performance.now() + 700;
              stageRef.current = index;
              setStage(index);
            }}
          >
            {step.name}
          </button>
        ))}
      </div>
      <figcaption aria-live="polite">{steps[stage].text}</figcaption>
    </figure>
  );
}
